import * as React from 'react'
import { useCallback, useEffect, useRef } from 'react'

import { cn } from '@/utils'

export interface AutoResizeTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  maxRows?: number
}

const AutoResizeTextarea = React.forwardRef<
  HTMLTextAreaElement,
  AutoResizeTextareaProps
>(({ className, maxRows = 4, onChange, value, ...props }, ref) => {
  const internalRef = useRef<HTMLTextAreaElement | null>(null)

  const setRefs = useCallback(
    (node: HTMLTextAreaElement | null) => {
      internalRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref],
  )

  const resize = useCallback(() => {
    const textarea = internalRef.current
    if (!textarea) return

    textarea.style.height = 'auto'
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight, 10) || 20
    const maxHeight = lineHeight * maxRows
    const contentHeight = textarea.scrollHeight
    textarea.style.height = `${Math.min(contentHeight, maxHeight)}px`
    // A box grown to fit its own text has nothing to scroll, and a scrollbar on
    // it reads as broken chrome.
    textarea.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden'
  }, [maxRows])

  useEffect(() => {
    resize()
  }, [value, resize])

  // A narrower box re-wraps the same text onto more lines. Without this the
  // height stays the one measured at the old width and the overflow stays
  // hidden, so the extra lines are both invisible and unreachable. Height
  // changes are ignored, or resize() would keep waking itself.
  useEffect(() => {
    const textarea = internalRef.current
    if (!textarea) return

    let measuredWidth = textarea.clientWidth
    const observer = new ResizeObserver(() => {
      if (textarea.clientWidth === measuredWidth) return
      measuredWidth = textarea.clientWidth
      resize()
    })
    observer.observe(textarea)
    return () => observer.disconnect()
  }, [resize])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e)
    resize()
  }

  return (
    <textarea
      ref={setRefs}
      className={cn(
        'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none overflow-y-auto',
        className,
      )}
      rows={1}
      value={value}
      onChange={handleChange}
      {...props}
    />
  )
})
AutoResizeTextarea.displayName = 'AutoResizeTextarea'

export { AutoResizeTextarea }
